"use client";

import { getPrimarySubaccountAddr } from "@decibeltrade/sdk";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isProduction } from "@/env";
import { addressesEqual } from "@/utils/address";

import { getSdkConfig } from "./dex-sdk";

const VIEW_AS_PARAM = "view_as";

// Shared no-op to satisfy no-empty-function lint rule
function noop() {
  // intentionally empty — used as default for inert context
}

interface ViewAsContextValue {
  isViewAsActive: boolean;
  isViewAsAllowed: boolean;
  viewAsAddress: string | undefined;
  viewAsPrimarySubaccountAddress: string | undefined;
  viewAsSubaccountAddress: string | undefined;
  switchViewAsSubaccount: (subAddr: string) => void;
  activateViewAs: (address: string) => void;
  exitViewAs: () => void;
}

const isViewAsAllowed = !isProduction;

const inertValue: ViewAsContextValue = {
  isViewAsActive: false,
  isViewAsAllowed,
  viewAsAddress: undefined,
  viewAsPrimarySubaccountAddress: undefined,
  viewAsSubaccountAddress: undefined,
  switchViewAsSubaccount: noop,
  activateViewAs: noop,
  exitViewAs: noop,
};

const ViewAsContext = createContext<ViewAsContextValue>(inertValue);

const sdkConfig = getSdkConfig();

export function ViewAsProvider({ children }: PropsWithChildren) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // State is the source of truth — survives client-side navigation.
  // The URL ?view_as= param seeds the initial value only.
  const [viewAsAddress, setViewAsAddress] = useState<string | undefined>(undefined);
  const [selectedSubaccount, setSelectedSubaccount] = useState<string | undefined>(undefined);

  // Seed from URL param on mount or when a genuinely new param appears (e.g. user pastes a new URL).
  // Track last consumed value in a ref so exit/switch don't get overridden by stale searchParams.
  const consumedUrlParamRef = useRef<string | undefined>(undefined);
  const urlParam = isViewAsAllowed ? (searchParams.get(VIEW_AS_PARAM) ?? undefined) : undefined;
  useEffect(() => {
    if (urlParam && urlParam !== consumedUrlParamRef.current) {
      consumedUrlParamRef.current = urlParam;
      setViewAsAddress(urlParam);
      setSelectedSubaccount(undefined);
    }
  }, [urlParam]);

  // Keep the URL param in sync — re-apply on navigation or when state changes
  useEffect(() => {
    if (!viewAsAddress) return;
    const currentParam = searchParams.get(VIEW_AS_PARAM);
    if (addressesEqual(currentParam ?? undefined, viewAsAddress)) return;
    const params = new URLSearchParams(searchParams.toString());
    params.set(VIEW_AS_PARAM, viewAsAddress);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [viewAsAddress, pathname, searchParams, router]);

  const viewAsPrimarySubaccountAddress = useMemo(() => {
    if (!viewAsAddress) return undefined;
    return getPrimarySubaccountAddr(
      viewAsAddress,
      sdkConfig.compatVersion,
      sdkConfig.deployment.package,
    );
  }, [viewAsAddress]);

  const viewAsSubaccountAddress = selectedSubaccount ?? viewAsPrimarySubaccountAddress;

  const activateViewAs = useCallback((address: string) => {
    setViewAsAddress(address);
    setSelectedSubaccount(undefined);
  }, []);

  const exitViewAs = useCallback(() => {
    setViewAsAddress(undefined);
    setSelectedSubaccount(undefined);
    // Clean up the URL param
    const params = new URLSearchParams(searchParams.toString());
    params.delete(VIEW_AS_PARAM);
    const qs = params.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
  }, [searchParams, router, pathname]);

  const switchViewAsSubaccount = useCallback((subAddr: string) => {
    setSelectedSubaccount(subAddr);
  }, []);

  const value = useMemo<ViewAsContextValue>(
    () =>
      viewAsAddress
        ? {
            isViewAsActive: true,
            isViewAsAllowed,
            viewAsAddress,
            viewAsPrimarySubaccountAddress,
            viewAsSubaccountAddress,
            switchViewAsSubaccount,
            activateViewAs,
            exitViewAs,
          }
        : { ...inertValue, activateViewAs },
    [
      viewAsAddress,
      viewAsPrimarySubaccountAddress,
      viewAsSubaccountAddress,
      switchViewAsSubaccount,
      activateViewAs,
      exitViewAs,
    ],
  );

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

export function useViewAs() {
  return useContext(ViewAsContext);
}
